'use client'

import { useAuthState } from '@/app/providers/auth'
import { userStateDateTimeToUTCString } from '@/components/page-specific/catering/common/helpers/date'
import type { Settable } from '@/components/page-specific/catering/common/utils/typing'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'

import * as dateFns from 'date-fns'
import { useCallback, useEffect } from 'react'

import type { UserBasketSettings } from '../domain/user-basket-settings'
import { computeDeliveryTimeSlots } from '../domain/user-basket-settings'
import { useAppStateStore } from '../store/appStateStore'

type Props = {
  resetDateTimeBefore?: { date: Date }
}

export type UserBasketSettingsState = Settable<UserBasketSettings>

export const useUserBasketSettingsState = (props?: Props): UserBasketSettingsState => {
  const { userState: settings, updateUserState: setSettings } = useAppStateStore()
  const authState = useAuthState()
  const useNewSavedAddresses = useFeatureFlag(SPLITS.USE_NEW_SAVED_ADDRESSED)
  const isCutOff5pm = useFeatureFlag(SPLITS.GM_CUTOFF_5PM)

  const updateSettings = useCallback(
    (newSettings: UserBasketSettings) => {
      const validNewSettings = {
        ...newSettings,
        ...((!newSettings.date || !newSettings.time) &&
          newSettings.headCount && {
            headCount: undefined,
          }),
      }

      setSettings(validNewSettings)
    },
    [setSettings],
  )

  useEffect(() => {
    if (!useNewSavedAddresses) return
    if (authState.type !== 'authenticated') return
    if (settings.location?.__typename !== 'Address') return
    if (!authState.user.account?.locations?.length) return
    const matchedAccountLocation = authState.user.account?.locations.find(
      (loc) =>
        settings.location?.__typename === 'Address' &&
        loc?.address.latitude?.toFixed(4) === settings.location.latitude?.toFixed(4) &&
        loc?.address.longitude?.toFixed(4) === settings.location.longitude?.toFixed(4),
    )
    if (!matchedAccountLocation) return
    updateSettings({
      ...settings,
      location: matchedAccountLocation,
    })
  }, [authState, settings, updateSettings, useNewSavedAddresses])

  const resetBeforeDate = props?.resetDateTimeBefore?.date
  useEffect(() => {
    if (!settings || !settings.date || !resetBeforeDate) return

    const selectedDateTimeString = userStateDateTimeToUTCString(settings.date, settings.time)
    if (selectedDateTimeString === null) return
    const selectedDateTime = new Date(selectedDateTimeString)
    if (dateFns.isAfter(selectedDateTime, resetBeforeDate)) return

    const selectableTimesOnTheDay = computeDeliveryTimeSlots({
      deliveryDate: new Date(settings.date),
      minAllowedDateTime: resetBeforeDate,
      useDeliveryTimes: isCutOff5pm,
    })

    const shouldClearDate = selectableTimesOnTheDay.length === 0
    const shouldClearTime = !!settings.time
    if (!shouldClearDate && !shouldClearTime) return
    updateSettings({
      ...settings,
      ...(shouldClearDate && { date: undefined }),
      ...(shouldClearTime && { time: undefined }),
    })
  }, [resetBeforeDate, settings, updateSettings, isCutOff5pm])

  return {
    value: settings,
    set: updateSettings,
  }
}
